import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth.store";
import { usePermissions } from "@/hooks/usePermissions";
import { useTaskStatuses } from "@/hooks/useTaskStatuses";
import { useOpenTask } from "@/hooks/useOpenTask";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { deleteTask, listTasks } from "@/api/tasks";
import { getMyProjects, listMembers } from "@/api/projects";
import type { MyProjectRead, ProjectMember, TaskRead } from "@/api/types";
import { TaskKanbanBoard } from "@/components/tasks/TaskKanbanBoard";
import { TaskDetailSheet } from "@/components/tasks/TaskDetailSheet";
import { TaskCreateDialog } from "@/components/tasks/TaskCreateDialog";

interface MyTasksKanbanProps {
  /** Current project slug — pre-selects its sub-tab when present in the
   *  user's projects, so opening the page lands on the project you're
   *  already in rather than an arbitrary first one. */
  defaultSlug?: string;
}

/** "My Tasks" + Kanban view. A cross-project kanban can't share one
 *  column set (each project has its own task-status catalog), so we
 *  show a sub-tab per project instead: each sub-tab is that project's
 *  own kanban, scoped to the current user's tasks. The List view keeps
 *  the flat cross-project feed — only Kanban gets the sub-tabs. */
export function MyTasksKanban({ defaultSlug }: MyTasksKanbanProps) {
  const [projects, setProjects] = useState<MyProjectRead[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [loadingProjects, setLoadingProjects] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getMyProjects()
      .then((ps) => {
        if (cancelled) return;
        setProjects(ps);
        // Land on the project you're already in, else the first one.
        setSelected((prev) => {
          if (prev && ps.some((p) => p.slug === prev)) return prev;
          if (defaultSlug && ps.some((p) => p.slug === defaultSlug)) {
            return defaultSlug;
          }
          return ps[0]?.slug ?? null;
        });
      })
      .catch(() => {
        if (!cancelled) setProjects([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingProjects(false);
      });
    return () => {
      cancelled = true;
    };
  }, [defaultSlug]);

  if (loadingProjects) {
    return (
      <div className="flex items-center justify-center rounded-plane bg-white py-16 text-sm text-slate-400 shadow-plane">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="rounded-plane bg-white py-16 text-center text-sm text-slate-500 shadow-plane">
        No projects to show.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Project sub-tabs — one kanban per project, since columns are
       *  the project's own task-status catalog. Hidden when there's only
       *  one project (a lone tab is just noise). */}
      {projects.length > 1 && (
        <div
          role="tablist"
          aria-label="Projects"
          className="flex flex-wrap items-center gap-1 border-b border-slate-200"
        >
          {projects.map((p) => {
            const active = selected === p.slug;
            return (
              <button
                key={p.slug}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setSelected(p.slug)}
                className={cn(
                  "-mb-px border-b-2 px-3 py-2 text-sm transition-colors",
                  active
                    ? "border-indigo-500 font-medium text-indigo-600"
                    : "border-transparent text-slate-500 hover:text-slate-700",
                )}
              >
                {p.name}
              </button>
            );
          })}
        </div>
      )}

      {selected && <ProjectMyTasksBoard key={selected} slug={selected} />}
    </div>
  );
}

/** One project's kanban, scoped to the current user's tasks. Self
 *  contained: it loads that project's task-status catalog + the user's
 *  tasks in it, and owns the detail / create / edit / delete flows so a
 *  sub-tab in another project never leaks the page project's slug. */
function ProjectMyTasksBoard({ slug }: { slug: string }) {
  const perms = usePermissions();
  const currentUser = useAuthStore((s) => s.user);
  const confirm = useConfirm();
  const taskStatuses = useTaskStatuses(slug);

  const [tasks, setTasks] = useState<TaskRead[]>([]);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [status, setStatus] = useState<"loading" | "ok" | "error">("loading");
  const [detailTask, setDetailTask] = useOpenTask(`my-tasks:${slug}`, tasks);
  const [editTask, setEditTask] = useState<TaskRead | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createDefaultStatus, setCreateDefaultStatus] = useState<
    string | null
  >(null);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const res = await listTasks(slug, {
        assigned_to: "me",
        page_size: 500,
      });
      setTasks(res.items);
      setStatus("ok");
    } catch {
      setStatus("error");
    }
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!perms.canEditTasks && !perms.canCreateTasks) return;
    let cancelled = false;
    listMembers(slug)
      .then((m) => !cancelled && setMembers(m))
      .catch(() => !cancelled && setMembers([]));
    return () => {
      cancelled = true;
    };
  }, [slug, perms.canEditTasks, perms.canCreateTasks]);

  async function handleDelete(task: TaskRead) {
    const ok = await confirm({
      title: "Delete task?",
      description: `"${task.title}" will be permanently deleted. This cannot be undone.`,
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      variant: "warning",
    });
    if (!ok) return;
    try {
      await deleteTask(slug, task.id);
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      toast.success("Task deleted");
    } catch (err) {
      toast.error(extractMessage(err));
    }
  }

  if (status === "error") {
    return (
      <div className="flex items-center justify-between rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
        <p className="text-sm font-medium text-rose-700">
          Couldn&rsquo;t load this project&rsquo;s tasks.
        </p>
        <button
          type="button"
          onClick={() => void load()}
          className="rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-100"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <TaskKanbanBoard
        tasks={tasks}
        projectSlug={slug}
        canEdit={perms.canEditTasks}
        canDelete={perms.canDeleteTasks}
        loading={status === "loading"}
        statuses={taskStatuses.statuses}
        onTaskUpdated={(updated) =>
          setTasks((prev) =>
            prev.map((t) => (t.id === updated.id ? updated : t)),
          )
        }
        onOpenDetail={(t) => setDetailTask(t)}
        onAskDelete={
          perms.canDeleteTasks ? (t) => void handleDelete(t) : undefined
        }
        onAddTask={(col) => {
          setCreateDefaultStatus(
            col.apply.kind === "status" ? col.apply.statusKey : null,
          );
          setCreateOpen(true);
        }}
      />

      <TaskDetailSheet
        task={detailTask}
        open={!!detailTask}
        onOpenChange={(o) => {
          if (!o) setDetailTask(null);
        }}
        projectSlug={slug}
        onTaskUpdated={(updated) => {
          setTasks((prev) =>
            prev.map((t) => (t.id === updated.id ? updated : t)),
          );
          setDetailTask(updated);
        }}
        onTaskDeleted={(id) => {
          setTasks((prev) => prev.filter((t) => t.id !== id));
          setDetailTask(null);
        }}
        onAskEdit={
          perms.canEditTasks
            ? (t) => {
                setDetailTask(null);
                setEditTask(t);
              }
            : undefined
        }
      />

      {perms.canCreateTasks && (
        <TaskCreateDialog
          projectSlug={slug}
          open={createOpen}
          onOpenChange={(o) => {
            setCreateOpen(o);
            if (!o) setCreateDefaultStatus(null);
          }}
          members={members}
          defaultAssigneeId={currentUser?.id ?? ""}
          defaultStatus={createDefaultStatus ?? taskStatuses.initialKey ?? null}
          taskStatuses={taskStatuses.statuses}
          onSaved={(created) => {
            // Only land it on this board if it's actually mine — the
            // form lets the user pick a different assignee.
            if (created.assigned_to_id === currentUser?.id) {
              setTasks((prev) => {
                const idx = prev.findIndex((t) => t.id === created.id);
                if (idx >= 0) {
                  const next = prev.slice();
                  next[idx] = created;
                  return next;
                }
                return [created, ...prev];
              });
            }
            void load();
          }}
        />
      )}

      {perms.canEditTasks && editTask && (
        <TaskCreateDialog
          mode="edit"
          task={editTask}
          projectSlug={slug}
          open={!!editTask}
          onOpenChange={(o) => {
            if (!o) setEditTask(null);
          }}
          members={members}
          onSaved={(updated) => {
            setTasks((prev) =>
              prev.map((t) => (t.id === updated.id ? updated : t)),
            );
            setEditTask(null);
            void load();
          }}
        />
      )}
    </>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Something went wrong.";
}
